/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import { ISbStoryData } from '@storyblok/react/rsc'
import { useAtomValue } from 'jotai'

import { BreadcrumbItem, Breadcrumbs } from '@/components/e-commerce/Breadcrumbs'
import { routing } from '@/i18n/routing'
import { storyAtom } from '@/store/story'

/** Props for the Storyblok breadcrumbs component */
type StoryblokBreadcrumbsProps = {
  /** The text to use for the home link (default: 'Home') */
  homeText?: string
}

/**
 * Storyblok-specific breadcrumbs component that generates breadcrumb items from the current story.
 */
const StoryblokBreadcrumbs = ({ homeText = 'Home' }: StoryblokBreadcrumbsProps) => {
  const story = useAtomValue(storyAtom)

  if (!story) return null

  /**
   * Removes the language prefix (e.g., '/es') and returns the non-i18n base slug from the full_slug.
   *
   * @param fullSlug - the full slug, with a potential locale prefix
   */
  const stripLocaleIfPresent = (fullSlug: string): string => {
    const parts = fullSlug.split('/')

    if (parts.length > 1) {
      const potentialLocale = parts[0]
      const isLocale = (routing.locales as ReadonlyArray<string>).includes(potentialLocale)

      if (isLocale) {
        return parts.slice(1).join('/')
      }
    }

    return fullSlug
  }

  /**
   * Generates breadcrumb items based on the current Storyblok story.
   */
  const generateBreadcrumbs = (story: ISbStoryData): Array<BreadcrumbItem> => {
    const breadcrumbs: BreadcrumbItem[] = []
    const slug = stripLocaleIfPresent(story.full_slug)

    // Handle different route types
    if (slug.startsWith('blog/')) {
      if (story.content?.primary_category) {
        breadcrumbs.push(
          {
            label: 'Blog',
            path: '/blog',
          },
          {
            label: story.content.primary_category.name,
            path: `/blog/category/${story.content.primary_category.slug}`,
          },
        )
      }
      breadcrumbs.push({
        label: story.name,
        path: `/${slug}`,
      })
    } else if (slug.startsWith('activities/')) {
      breadcrumbs.push(
        {
          label: 'Activities',
          path: '/activities',
        },
        {
          label: story.name,
          path: `/${slug}`,
        },
      )
    } else if (slug.startsWith('sectors/')) {
      breadcrumbs.push(
        {
          label: 'Sectors',
          path: '/sectors',
        },
        {
          label: story.name,
          path: `/${slug}`,
        },
      )
    } else if (slug.startsWith('kits/')) {
      breadcrumbs.push(
        {
          label: 'Kits',
          path: '/kits',
        },
        {
          label: story.name,
          path: `/${slug}`,
        },
      )
    }

    // Mark the last item as current
    if (breadcrumbs.length > 0) {
      breadcrumbs[breadcrumbs.length - 1].current = true
    }

    return breadcrumbs
  }

  const items = generateBreadcrumbs(story)
  return <Breadcrumbs items={items} homeText={homeText} />
}

export { StoryblokBreadcrumbs }
